import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/utils';
import {
    CreditCard, RefreshCw, Search, Crown, Star, Zap,
    Coins, Gift, Loader2, ExternalLink, Calendar,
    ChevronDown, ChevronUp, X, Clock, AlertCircle,
    CheckCircle2, XCircle, ArrowUpRight,
} from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Subscription {
    userId: string;
    username: string;
    email: string;
    planId: string;
    status: string;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    tierLevel: number;
    currentTokens: number;
    tokensUsedThisMonth: number;
    tokensGrantedThisMonth: number;
    createdAt: string | null;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useSubscriptions() {
    return useQuery<Subscription[]>({
        queryKey: ['admin', 'subscriptions'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_all_subscriptions');
            if (error) throw error;
            return normalizeSubscriptions(data);
        },
        staleTime: 60_000,
    });
}

function useSyncSubscription() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (userId: string) => {
            const { data, error } = await apiClient.post('/stripe-sync-subscription', { userId });
            if (error) throw error;
            return data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] }),
    });
}

function useGrantTokens() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId, amount, reason }: {
            userId: string;
            amount: number;
            reason: string;
        }) => {
            const { data, error } = await dbClient.rpc('admin_grant_tokens', {
                p_target_user_id: userId,
                p_amount: amount,
                p_reason: reason,
            });
            if (error) throw error;
            return data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] }),
    });
}

const PRO_PERIOD_OPTIONS = [
    { value: '1m', label: '1 mes' },
    { value: '3m', label: '3 meses' },
    { value: '6m', label: '6 meses' },
    { value: '12m', label: '12 meses' },
    { value: 'unlimited', label: 'Ilimitado' },
] as const;

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function useGrantProByPeriod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            userId,
            periodKey,
            reason,
        }: {
            userId: string;
            periodKey: string;
            reason: string;
        }) => {
            const { data, error } = await dbClient.rpc('admin_grant_pro_by_period', {
                p_target_user_id: userId,
                p_period_key: periodKey,
                p_reason: reason || 'admin_pro_grant',
            });
            if (error) throw error;
            const row = Array.isArray(data) ? data[0] : data;
            return row as { success?: boolean; message?: string; current_period_end?: string | null };
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] }),
    });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function normalizeSubscriptions(data: unknown): Subscription[] {
    const arr = normalizeRpcResponse(data) as any[];
    return arr.map(d => ({
        userId: d.user_id || d.userId,
        username: d.username || d.display_name || 'Sin nombre',
        email: d.email || '',
        planId: d.plan_id || d.planId || 'free',
        status: d.status || 'inactive',
        stripeCustomerId: d.stripe_customer_id || null,
        stripeSubscriptionId: d.stripe_subscription_id || null,
        currentPeriodEnd: d.current_period_end || null,
        cancelAtPeriodEnd: d.cancel_at_period_end || false,
        tierLevel: d.subscription_tier_level || d.tier_level || 0,
        currentTokens: d.current_tokens || 0,
        tokensUsedThisMonth: d.tokens_used_this_month || 0,
        tokensGrantedThisMonth: d.tokens_granted_this_month || 0,
        createdAt: d.created_at || null,
    }));
}

const PLAN_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; badgeVariant: 'success' | 'warning' | 'info' | 'neutral' }> = {
    premium: { label: 'Premium', icon: <Crown size={14} />, color: 'text-amber-400', badgeVariant: 'warning' },
    pro: { label: 'Pro', icon: <Star size={14} />, color: 'text-ocean-400', badgeVariant: 'info' },
    free: { label: 'Basic', icon: <Zap size={14} />, color: 'text-muted-foreground', badgeVariant: 'neutral' },
};

function getPlanConfig(planId: string) {
    return PLAN_CONFIG[planId] || PLAN_CONFIG.free;
}

function getStatusVariant(status: string, cancelAtPeriodEnd: boolean): 'success' | 'warning' | 'error' | 'neutral' {
    if (status === 'active' && cancelAtPeriodEnd) return 'warning';
    if (status === 'active') return 'success';
    if (status === 'past_due') return 'error';
    return 'neutral';
}

function getStatusLabel(status: string, cancelAtPeriodEnd: boolean): string {
    if (status === 'active' && cancelAtPeriodEnd) return 'Cancela al final';
    if (status === 'active') return 'Activa';
    if (status === 'past_due') return 'Pago pendiente';
    if (status === 'canceled') return 'Cancelada';
    if (status === 'inactive') return 'Inactiva';
    return status;
}

function formatRelativeDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    try {
        return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: es });
    } catch {
        return '—';
    }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SubscriptionsPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [grantDialog, setGrantDialog] = useState<{ userId: string; username: string; currentTokens: number } | null>(null);
    const [grantAmount, setGrantAmount] = useState('10');
    const [grantReason, setGrantReason] = useState('soporte');
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [proGrantLookup, setProGrantLookup] = useState('');
    const [proGrantSelected, setProGrantSelected] = useState<Subscription | null>(null);
    const [proPeriod, setProPeriod] = useState<(typeof PRO_PERIOD_OPTIONS)[number]['value']>('1m');
    const [proGrantReason, setProGrantReason] = useState('admin_pro_grant');
    const [proGrantFeedback, setProGrantFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

    const { data: subscriptions, isLoading, isFetching, refetch } = useSubscriptions();
    const syncMutation = useSyncSubscription();
    const grantMutation = useGrantTokens();
    const grantProMutation = useGrantProByPeriod();

    // Auto-dismiss success message
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const proLookupTrim = proGrantLookup.trim();
    const proGrantSuggestions = useMemo(() => {
        const list = subscriptions || [];
        if (proLookupTrim.length < 2 || UUID_RE.test(proLookupTrim) || proGrantSelected) return [];
        const q = proLookupTrim.toLowerCase();
        return list
            .filter(s =>
                s.username.toLowerCase().includes(q)
                || s.email.toLowerCase().includes(q),
            )
            .slice(0, 12);
    }, [subscriptions, proLookupTrim, proGrantSelected]);

    const resolvedProUserId =
        proGrantSelected?.userId
        ?? (UUID_RE.test(proLookupTrim) ? proLookupTrim : '');

    // Filter
    const filtered = (subscriptions || []).filter(s => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return s.username.toLowerCase().includes(q)
            || s.email.toLowerCase().includes(q)
            || s.planId.toLowerCase().includes(q);
    });

    // Stats
    const totalSubs = (subscriptions || []).length;
    const totalActive = (subscriptions || []).filter(s => s.status === 'active').length;
    const totalPro = (subscriptions || []).filter(s => s.planId === 'pro' && s.status === 'active').length;
    const totalPremium = (subscriptions || []).filter(s => s.planId === 'premium' && s.status === 'active').length;
    const totalTokens = (subscriptions || []).reduce((sum, s) => sum + s.currentTokens, 0);

    function handleGrantTokens() {
        if (!grantDialog) return;
        const amount = parseInt(grantAmount) || 10;
        grantMutation.mutate({
            userId: grantDialog.userId,
            amount,
            reason: grantReason,
        }, {
            onSuccess: () => {
                setSuccessMessage(`✅ ${amount} tokens concedidos a ${grantDialog.username}`);
                setGrantDialog(null);
                setGrantAmount('10');
                setGrantReason('soporte');
            },
        });
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Suscripciones"
                description={`Gestión de planes Premium y tokens — ${totalSubs} suscriptores`}
            >
                <button
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
                    Actualizar
                </button>
            </PageHeader>

            {/* Success Toast */}
            {successMessage && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 text-sm text-emerald-400 flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                    <CheckCircle2 size={16} />
                    {successMessage}
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={<CreditCard size={20} />}
                    iconClass="bg-emerald-500/10 text-emerald-500"
                    label="Activas"
                    value={totalActive.toString()}
                    sub={`de ${totalSubs} total`}
                />
                <StatCard
                    icon={<Star size={20} />}
                    iconClass="bg-ocean-400/10 text-ocean-400"
                    label="Plan Pro"
                    value={totalPro.toString()}
                />
                <StatCard
                    icon={<Crown size={20} />}
                    iconClass="bg-amber-400/10 text-amber-400"
                    label="Plan Premium"
                    value={totalPremium.toString()}
                />
                <StatCard
                    icon={<Coins size={20} />}
                    iconClass="bg-purple-400/10 text-purple-400"
                    label="Tokens Totales"
                    value={totalTokens.toLocaleString('es-ES')}
                    sub="en circulación"
                />
            </div>

            {/* Activar Pro manual */}
            <div className="rounded-xl border border-ocean-400/30 bg-card p-5 space-y-4">
                <div>
                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                        <Star size={18} className="text-ocean-400" />
                        Activar Pro (soporte)
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Busca por nombre de usuario o email (lista de perfiles cargada) o pega el UUID si lo tienes.
                        Si ya tiene Pro con fecha de fin, el período se alarga desde esa fecha.
                        No aplica a Premium. Tras el fin del período manual, Stripe puede volver a fijar el plan.
                    </p>
                </div>
                {proGrantFeedback && (
                    <div
                        className={cn(
                            'rounded-lg px-3 py-2 text-sm border',
                            proGrantFeedback.type === 'ok'
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                                : 'bg-destructive/10 text-destructive border-destructive/25',
                        )}
                    >
                        {proGrantFeedback.text}
                    </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="sm:col-span-2 space-y-1.5 relative">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Usuario</label>
                        <input
                            type="text"
                            autoComplete="off"
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            placeholder="Nombre, email o UUID…"
                            value={proGrantLookup}
                            onChange={(e) => {
                                setProGrantLookup(e.target.value);
                                setProGrantSelected(null);
                                setProGrantFeedback(null);
                            }}
                        />
                        {proGrantSuggestions.length > 0 && (
                            <ul
                                className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-border bg-card shadow-lg text-sm"
                                role="listbox"
                            >
                                {proGrantSuggestions.map((s) => (
                                    <li key={s.userId} role="option">
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-muted/80 border-b border-border/50 last:border-0"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                setProGrantSelected(s);
                                                setProGrantLookup(
                                                    s.username && s.username !== 'Sin nombre'
                                                        ? s.username
                                                        : s.email || s.userId,
                                                );
                                                setProGrantFeedback(null);
                                            }}
                                        >
                                            <span className="font-medium text-foreground">{s.username}</span>
                                            <span className="block text-xs text-muted-foreground truncate">{s.email}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {proLookupTrim.length >= 2
                            && !proGrantSelected
                            && !UUID_RE.test(proLookupTrim)
                            && proGrantSuggestions.length === 0
                            && (subscriptions?.length ?? 0) > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">Sin coincidencias en la lista.</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Duración</label>
                        <select
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            value={proPeriod}
                            onChange={(e) => setProPeriod(e.target.value as (typeof PRO_PERIOD_OPTIONS)[number]['value'])}
                        >
                            {PRO_PERIOD_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nota</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            value={proGrantReason}
                            onChange={(e) => setProGrantReason(e.target.value)}
                            placeholder="auditoría / motivo"
                        />
                    </div>
                </div>
                <button
                    type="button"
                    disabled={grantProMutation.isPending || !resolvedProUserId}
                    onClick={() => {
                        setProGrantFeedback(null);
                        grantProMutation.mutate(
                            {
                                userId: resolvedProUserId,
                                periodKey: proPeriod,
                                reason: proGrantReason || 'admin_pro_grant',
                            },
                            {
                                onSuccess: (row) => {
                                    if (row?.success) {
                                        const end = row.current_period_end
                                            ? format(new Date(row.current_period_end), 'd MMM yyyy', { locale: es })
                                            : 'sin fecha de fin';
                                        setProGrantFeedback({
                                            type: 'ok',
                                            text: `${row.message ?? 'Pro activado.'} Vence: ${end}.`,
                                        });
                                        setProGrantLookup('');
                                        setProGrantSelected(null);
                                    } else {
                                        setProGrantFeedback({
                                            type: 'err',
                                            text: row?.message ?? 'No se pudo activar Pro.',
                                        });
                                    }
                                },
                                onError: (e: Error) => {
                                    setProGrantFeedback({ type: 'err', text: e.message });
                                },
                            },
                        );
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-ocean-500 text-white hover:bg-ocean-500/90 disabled:opacity-50 transition-colors"
                >
                    {grantProMutation.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <Star size={16} />
                    )}
                    Activar Pro
                </button>
                {proLookupTrim.length > 0 && !resolvedProUserId && (
                    <p className="text-xs text-destructive">
                        Elige un usuario de las sugerencias o introduce un UUID completo.
                    </p>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    placeholder="Buscar por nombre, email o plan..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                />
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto max-h-[calc(100vh-380px)] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                            <tr className="border-b border-border bg-card">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Usuario</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Plan</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Estado</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">
                                    <div className="flex items-center gap-1 justify-end">
                                        <Coins size={12} /> Tokens
                                    </div>
                                </th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Stripe</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Renovación</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-card">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center text-muted-foreground">
                                        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                                        Cargando suscripciones...
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center text-muted-foreground">
                                        <CreditCard size={24} className="mx-auto mb-2 text-muted-foreground/40" />
                                        {searchQuery
                                            ? <>No se encontraron suscripciones para "{searchQuery}"</>
                                            : 'No hay suscripciones registradas'
                                        }
                                    </td>
                                </tr>
                            ) : (
                                filtered.map(sub => (
                                    <SubscriptionRow
                                        key={sub.userId}
                                        sub={sub}
                                        isExpanded={expandedUserId === sub.userId}
                                        onToggle={() => setExpandedUserId(
                                            expandedUserId === sub.userId ? null : sub.userId
                                        )}
                                        onGrantTokens={() => setGrantDialog({
                                            userId: sub.userId,
                                            username: sub.username,
                                            currentTokens: sub.currentTokens,
                                        })}
                                        onSync={() => syncMutation.mutate(sub.userId)}
                                        isSyncing={syncMutation.isPending}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Grant Tokens Dialog */}
            {grantDialog && (
                <GrantTokensDialog
                    username={grantDialog.username}
                    currentTokens={grantDialog.currentTokens}
                    amount={grantAmount}
                    reason={grantReason}
                    onAmountChange={setGrantAmount}
                    onReasonChange={setGrantReason}
                    onSubmit={handleGrantTokens}
                    onCancel={() => setGrantDialog(null)}
                    isPending={grantMutation.isPending}
                />
            )}
        </div>
    );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ icon, iconClass, label, value, sub }: {
    icon: React.ReactNode;
    iconClass: string;
    label: string;
    value: string;
    sub?: string;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4">
            <div className={cn('p-2.5 rounded-lg', iconClass)}>{icon}</div>
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                {sub && <p className="text-[10px] text-muted-foreground/60">{sub}</p>}
            </div>
        </div>
    );
}

// ─── Subscription Row ───────────────────────────────────────────────────────

function SubscriptionRow({ sub, isExpanded, onToggle, onGrantTokens, onSync, isSyncing }: {
    sub: Subscription;
    isExpanded: boolean;
    onToggle: () => void;
    onGrantTokens: () => void;
    onSync: () => void;
    isSyncing: boolean;
}) {
    const plan = getPlanConfig(sub.planId);
    const statusVariant = getStatusVariant(sub.status, sub.cancelAtPeriodEnd);
    const statusLabel = getStatusLabel(sub.status, sub.cancelAtPeriodEnd);
    const hasStripe = !!sub.stripeCustomerId;
    const tokenPercent = sub.currentTokens > 0 ? Math.min(100, (sub.tokensUsedThisMonth / (sub.currentTokens + sub.tokensUsedThisMonth)) * 100) : 0;

    return (
        <>
            <tr
                className={cn(
                    'cursor-pointer transition-colors',
                    isExpanded ? 'bg-muted/20' : 'hover:bg-muted/10',
                )}
                onClick={onToggle}
            >
                {/* User */}
                <td className="px-4 py-3">
                    <div>
                        <p className="font-medium text-foreground">{sub.username}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">{sub.email}</p>
                    </div>
                </td>

                {/* Plan */}
                <td className="px-4 py-3">
                    <div className={cn('flex items-center gap-1.5', plan.color)}>
                        {plan.icon}
                        <span className="font-medium">{plan.label}</span>
                    </div>
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                    <StatusBadge status={statusLabel} variant={statusVariant} />
                </td>

                {/* Tokens */}
                <td className="px-4 py-3 text-right">
                    <div>
                        <div className="flex items-center gap-1.5 justify-end">
                            <Coins size={13} className={cn(
                                sub.currentTokens > 0 ? 'text-amber-400' : 'text-muted-foreground/30'
                            )} />
                            <span className={cn(
                                'font-bold tabular-nums',
                                sub.currentTokens > 0 ? 'text-foreground' : 'text-muted-foreground/50'
                            )}>
                                {sub.currentTokens}
                            </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                            {sub.tokensUsedThisMonth > 0 && (
                                <span>Usado: {sub.tokensUsedThisMonth}</span>
                            )}
                            {sub.tokensGrantedThisMonth > 0 && (
                                <span className="text-emerald-400"> · +{sub.tokensGrantedThisMonth} regalo</span>
                            )}
                        </p>
                    </div>
                </td>

                {/* Stripe */}
                <td className="px-4 py-3">
                    {hasStripe ? (
                        <div className="flex items-center gap-1.5">
                            <CheckCircle2 size={13} className="text-emerald-400" />
                            <span className="text-xs text-muted-foreground font-mono truncate max-w-[120px]">
                                {sub.stripeCustomerId?.slice(-8)}
                            </span>
                        </div>
                    ) : (
                        <span className="text-xs text-muted-foreground/40">Sin Stripe</span>
                    )}
                </td>

                {/* Renewal */}
                <td className="px-4 py-3">
                    {sub.currentPeriodEnd ? (
                        <div>
                            <p className="text-xs text-muted-foreground whitespace-nowrap">
                                {format(new Date(sub.currentPeriodEnd), 'd MMM yyyy', { locale: es })}
                            </p>
                            <p className="text-[10px] text-muted-foreground/60">
                                {formatRelativeDate(sub.currentPeriodEnd)}
                            </p>
                        </div>
                    ) : sub.planId === 'pro' && sub.status === 'active' ? (
                        <span className="text-xs text-ocean-400 font-medium">Ilimitado</span>
                    ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                </td>

                {/* Actions */}
                <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={onSync}
                            disabled={isSyncing}
                            title="Sincronizar con Stripe"
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={cn(isSyncing && 'animate-spin')} />
                        </button>
                        <button
                            onClick={onGrantTokens}
                            title="Regalar tokens"
                            className="p-1.5 rounded-md text-muted-foreground hover:text-amber-400 hover:bg-amber-400/10 transition-colors"
                        >
                            <Gift size={14} />
                        </button>
                        <ChevronDown
                            size={14}
                            className={cn(
                                'text-muted-foreground transition-transform ml-1',
                                isExpanded && 'rotate-180'
                            )}
                        />
                    </div>
                </td>
            </tr>

            {/* Expanded Details */}
            {isExpanded && (
                <tr>
                    <td colSpan={7} className="px-4 pb-4 pt-2 bg-muted/10">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Token Usage Card */}
                            <div className="rounded-lg border border-border bg-card/50 p-3">
                                <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-2">
                                    Uso de Tokens (período actual)
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Balance actual</span>
                                        <span className="font-bold text-foreground">{sub.currentTokens}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Usados este mes</span>
                                        <span className="text-foreground">{sub.tokensUsedThisMonth}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Regalados este mes</span>
                                        <span className={cn(
                                            sub.tokensGrantedThisMonth > 0 ? 'text-emerald-400' : 'text-foreground'
                                        )}>
                                            {sub.tokensGrantedThisMonth > 0 ? `+${sub.tokensGrantedThisMonth}` : '0'}
                                        </span>
                                    </div>
                                    {/* Token usage bar */}
                                    <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                                        <div
                                            className="h-full bg-ocean-400 rounded-full transition-all"
                                            style={{ width: `${tokenPercent}%` }}
                                        />
                                    </div>
                                    <p className="text-[10px] text-muted-foreground/60 text-right">
                                        {tokenPercent.toFixed(0)}% consumido
                                    </p>
                                </div>
                            </div>

                            {/* Stripe Details */}
                            <div className="rounded-lg border border-border bg-card/50 p-3">
                                <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-2">
                                    Detalles de Stripe
                                </h4>
                                <div className="space-y-2">
                                    <DetailRow label="Customer ID" value={sub.stripeCustomerId || 'N/A'} copyable />
                                    <DetailRow label="Subscription ID" value={sub.stripeSubscriptionId || 'N/A'} copyable />
                                    <DetailRow label="Cancelación" value={sub.cancelAtPeriodEnd ? 'Sí, al final del período' : 'No'} />
                                    <DetailRow label="Tier Level" value={sub.tierLevel.toString()} />
                                </div>
                            </div>

                            {/* Subscription Info */}
                            <div className="rounded-lg border border-border bg-card/50 p-3">
                                <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold mb-2">
                                    Info de Suscripción
                                </h4>
                                <div className="space-y-2">
                                    <DetailRow label="User ID" value={sub.userId} copyable />
                                    <DetailRow
                                        label="Período actual"
                                        value={sub.currentPeriodEnd
                                            ? `hasta ${format(new Date(sub.currentPeriodEnd), "d 'de' MMMM yyyy", { locale: es })}`
                                            : 'N/A'
                                        }
                                    />
                                    <DetailRow
                                        label="Creada"
                                        value={sub.createdAt
                                            ? format(new Date(sub.createdAt), "d MMM yyyy HH:mm", { locale: es })
                                            : 'N/A'
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── Detail Row ─────────────────────────────────────────────────────────────

function DetailRow({ label, value, copyable }: {
    label: string;
    value: string;
    copyable?: boolean;
}) {
    const handleCopy = () => {
        if (copyable && value !== 'N/A') navigator.clipboard.writeText(value);
    };

    return (
        <div
            className={cn(
                'flex justify-between text-sm gap-2',
                copyable && value !== 'N/A' && 'cursor-pointer hover:opacity-80'
            )}
            onClick={handleCopy}
            title={copyable && value !== 'N/A' ? 'Clic para copiar' : undefined}
        >
            <span className="text-muted-foreground text-xs">{label}</span>
            <span className={cn(
                'text-foreground text-xs truncate max-w-[200px]',
                copyable && 'font-mono'
            )}>
                {value}
            </span>
        </div>
    );
}

// ─── Grant Tokens Dialog ────────────────────────────────────────────────────

function GrantTokensDialog({ username, currentTokens, amount, reason, onAmountChange, onReasonChange, onSubmit, onCancel, isPending }: {
    username: string;
    currentTokens: number;
    amount: string;
    reason: string;
    onAmountChange: (v: string) => void;
    onReasonChange: (v: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
    isPending: boolean;
}) {
    const parsedAmount = parseInt(amount) || 0;
    const newBalance = currentTokens + parsedAmount;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                        <Gift size={18} className="inline mr-2 text-amber-400" />
                        Regalar Tokens
                    </h3>
                    <button onClick={onCancel} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted">
                        <X size={16} />
                    </button>
                </div>

                <p className="text-sm text-muted-foreground mb-1">
                    Usuario: <span className="font-medium text-foreground">{username}</span>
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                    Balance actual: <span className="font-mono font-medium text-foreground">{currentTokens}</span> tokens
                </p>

                <div className="space-y-3">
                    {/* Quick amounts */}
                    <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cantidad</label>
                        <div className="flex gap-2 mt-1.5 mb-1.5">
                            {[5, 10, 25, 50, 100].map(n => (
                                <button
                                    key={n}
                                    onClick={() => onAmountChange(n.toString())}
                                    className={cn(
                                        'flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors',
                                        amount === n.toString()
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                        <input
                            type="number"
                            value={amount}
                            onChange={e => onAmountChange(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            min={1}
                            max={10000}
                            placeholder="Cantidad personalizada..."
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Razón</label>
                        <input
                            type="text"
                            value={reason}
                            onChange={e => onReasonChange(e.target.value)}
                            className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            placeholder="Razón del regalo..."
                        />
                    </div>

                    {/* Preview */}
                    {parsedAmount > 0 && (
                        <div className="bg-muted/50 rounded-lg px-3 py-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Nuevo balance:</span>
                                <span className="font-bold text-foreground">{newBalance} tokens</span>
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground/60 mt-0.5">
                                <span>{currentTokens} actual + {parsedAmount} regalo</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 mt-5">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={isPending || parsedAmount <= 0}
                        className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {isPending ? (
                            <Loader2 size={14} className="animate-spin mx-auto" />
                        ) : (
                            <>
                                <Gift size={14} className="inline mr-1.5" />
                                Enviar {parsedAmount > 0 ? `${parsedAmount} tokens` : '...'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
